import { useEffect, useContext } from 'react';
import * as analytics from 'utilities/analytics';

import {
  usePlayerFromContext,
  SystemEvents,
  Player,
  Video,
  usePlayingStatus,
  NewReactionPayload,
  UiEvents,
  ReactionClickedPayload,
  DeleteReactionPayload,
  VideoStepPayload,
} from '@loomhq/createhq';
import {
  EMOJI_REACTION_CLICKED,
  EMOJI_REACTION_CREATED,
  EMOJI_REACTION_DELETED,
  FULL_SCREEN_CLICK,
  PLAYBACK_RATE_CHANGE,
  SHOW_VIDEO_PLAYER_END_SCREEN,
  TOGGLE_CAPTIONS_OFF,
  TOGGLE_CAPTIONS_ON,
  VIDEO_PLAYER_ENTER_COMMENT_MODE,
  VIDEO_PLAYER_STEP,
  VIDEO_PLAYER_VIDEO_PLAY_START,
  WATCH_AGAIN_CLICK,
} from 'constants/events';
import { STEP_BACK, STEP_FORWARD } from 'constants/videoPlayer';
import {
  SectionTitleContext,
  getSurfaceName,
} from 'contexts/SectionTitleContext';

function usePlayerEventsListener(video: Video): void {
  const player = usePlayerFromContext();
  const sectionTitle = useContext(SectionTitleContext);

  usePlayingStatusAnalytics(video);

  useEffect(() => {
    if (player?.started) {
      analytics.track(VIDEO_PLAYER_VIDEO_PLAY_START, {
        currentTime: player.duration,
        videoId: video.modelId,
        surface: getSurfaceName(sectionTitle),
        freshPlayer: true,
      });
    }
  }, [player?.started]);

  useEffect(() => {
    if (!player) {
      return;
    }

    const onSpeedChange = (speed: number) => {
      if (Player.rates.includes(speed)) {
        analytics.track(PLAYBACK_RATE_CHANGE, {
          playbackRate: speed,
          currentTime: player.duration,
          videoId: video.modelId,
        });
      }
    };

    const toggleClosedCaptions = () => {
      const event = player.closedCaptions
        ? TOGGLE_CAPTIONS_ON
        : TOGGLE_CAPTIONS_OFF;

      analytics.track(event, {
        currentUserIsOwner: video.isOwner,
        video_id: video.modelId,
        captions_visible: player.closedCaptions,
      });
    };

    const reactionCreated = (payload: NewReactionPayload) => {
      analytics.track(EMOJI_REACTION_CREATED, {
        video_id: video.modelId,
        sentiment: payload.type,
        freshPlayer: true,
      });
    };

    const watchAgainClicked = () => {
      analytics.track(WATCH_AGAIN_CLICK, {
        video_id: video.modelId,
      });
    };

    const emojiReactionClicked = (payload: ReactionClickedPayload) => {
      analytics.track(EMOJI_REACTION_CLICKED, {
        video_id: video.modelId,
        currentUserIsOwner: video.isOwner,
        sentiment: payload.type,
      });
    };

    const emojiReactionDeleted = (payload: DeleteReactionPayload) => {
      analytics.track(EMOJI_REACTION_DELETED, {
        reactor_id: payload.reactionId,
        currentUserIsOwner: video.isOwner,
        video_id: video.modelId,
        sentiment: payload.type,
      });
    };

    const fullScreenClicked = () => {
      analytics.track(FULL_SCREEN_CLICK);
    };

    const videoStepClicked = (payload: VideoStepPayload) => {
      analytics.track(VIDEO_PLAYER_STEP, {
        stepType: payload.direction === 'back' ? STEP_BACK : STEP_FORWARD,
        stepDuration: payload.amount,
        videoId: video.modelId,
      });
    };

    const onCommentMode = (isOpen: boolean) => {
      if (isOpen) {
        analytics.track(VIDEO_PLAYER_ENTER_COMMENT_MODE, {
          video_id: video.modelId,
          from_end_screen: player.status === 'ended',
        });
      }
    };

    player.on([SystemEvents.speed], onSpeedChange, false, true);
    player.on([SystemEvents.closedCaptions], toggleClosedCaptions);
    player.on([UiEvents.newReaction], reactionCreated);
    player.on([UiEvents.watchAgainClicked], watchAgainClicked);
    player.on([UiEvents.reactionClicked], emojiReactionClicked);
    player.on([UiEvents.deleteReaction], emojiReactionDeleted);
    player.on([UiEvents.fullScreenClicked], fullScreenClicked);
    player.on([UiEvents.videoStepClicked], videoStepClicked);
    player.on([UiEvents.commentFormToggle], onCommentMode);

    return () => {
      player.off([SystemEvents.speed], onSpeedChange);
      player.off([SystemEvents.closedCaptions], toggleClosedCaptions);
      player.off([UiEvents.newReaction], reactionCreated);
      player.off([UiEvents.watchAgainClicked], watchAgainClicked);
      player.off([UiEvents.reactionClicked], emojiReactionClicked);
      player.off([UiEvents.deleteReaction], emojiReactionDeleted);
      player.off([UiEvents.fullScreenClicked], fullScreenClicked);
      player.off([UiEvents.videoStepClicked], videoStepClicked);
      player.off([UiEvents.commentFormToggle], onCommentMode);
    };
  }, [player, video]);
}

function usePlayingStatusAnalytics(video: Video) {
  const { status } = usePlayingStatus(video.id);

  useEffect(() => {
    if (status === 'ended') {
      analytics.track(SHOW_VIDEO_PLAYER_END_SCREEN, {
        cta_type: 'large-cta',
        video_id: video.modelId,
        from_url: window.location.href,
      });
    }
  }, [status, video]);
}

export default usePlayerEventsListener;
